import {
  createAuthAPI,
  createPublicAPI,
} from 'lib/utils/fetch/create-api-factory';
import {
  type ReviewRequest,
  type ReviewRequestPublic,
  GetReviewsResponseSchema,
  ReviewResponseSchema,
  GetLatestReviewResponseSchema,
} from './review.schema';
import { TradeReviewMetricsResponseSchema } from './metrics.schema';

export const postReview = createAuthAPI<
  ReviewRequest,
  typeof ReviewResponseSchema
>({
  url: `${process.env.NEXT_PUBLIC_CONSUMER_SERVICE_URL}/review`,
  method: 'POST',
  schema: ReviewResponseSchema,
});

export const postReviewPublic = createPublicAPI<
  ReviewRequestPublic,
  typeof ReviewResponseSchema
>({
  url: `${process.env.NEXT_PUBLIC_CONSUMER_PUBLIC_URL}/review`,
  method: 'POST',
  schema: ReviewResponseSchema,
});

export const getTradeReviews = (
  companyId: number,
  page: number,
  token: string,
) =>
  createAuthAPI({
    url: `${process.env.NEXT_PUBLIC_CONSUMER_SERVICE_URL}/reviews/${companyId}?page=${page}`,
    method: 'GET',
    schema: GetReviewsResponseSchema,
  })({ token });

export const getTradeReviewsPublic = (companyId: number, page: number) =>
  createPublicAPI({
    url: `${process.env.NEXT_PUBLIC_CONSUMER_PUBLIC_URL}/reviews/${companyId}?page=${page}`,
    method: 'GET',
    schema: GetReviewsResponseSchema,
  })({});

export const getLatestTradeReviewPublic = (companyIds: number[]) => {
  return createPublicAPI({
    url: `${process.env.NEXT_PUBLIC_CONSUMER_PUBLIC_URL}/reviews/has-recent-reviews?companyIds=${companyIds.join(',')}`,
    method: 'GET',
    schema: GetLatestReviewResponseSchema,
  })({});
};

export const getConsumerReviews = (page: number, token: string) =>
  createAuthAPI({
    url: `${process.env.NEXT_PUBLIC_CONSUMER_SERVICE_URL}/reviews?page=${page}`,
    method: 'GET',
    schema: GetReviewsResponseSchema,
  })({ token });

export const getTradeMetricsPublic = async (companyId: number) => {
  const metricsResponse = await createPublicAPI({
    url: `${process.env.NEXT_PUBLIC_CONSUMER_PUBLIC_URL}/metrics/${companyId}`,
    method: 'GET',
    schema: TradeReviewMetricsResponseSchema,
    nullOnError: true,
  })({});

  if (!metricsResponse) return null;

  const {
    totalReviewCount: recentReviewCount,
    qualityOfWorkmanship: { scoreAverage: workmanship },
    reliabilityAndTimekeeping: { scoreAverage: reliability } = {},
    courtesy: { scoreAverage: courtesy } = {},
    communication: { scoreAverage: communication } = {},
    score: totalScore,
  } = metricsResponse.year;

  return {
    totalScore,
    recentReviewCount,
    workmanship,
    reliability,
    courtesy,
    communication,
  };
};
